import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  FaStore, FaExclamationTriangle, FaBoxes, FaTags, 
  FaShoppingCart, FaBarcode, FaBoxOpen, FaPlus,
  FaSearch, FaFilter, FaSortAmountDown, FaEllipsisV,
  FaTag, FaInfoCircle, FaChevronLeft, FaChevronRight
} from 'react-icons/fa';
import './ProductsList.css';
import './UI/SearchInput.css';

const ITEMS_PER_PAGE = 25;

// Dummy data for "experience" role
const dummyProductsList = [
  { id: 'exp001', title: 'Experience T-Shirt - Galaxy', images: [{ src: '/tshirtgalaxy.png' }], status: 'active', variants: [{ inventory_quantity: 15, title: 'M / Black' }, { inventory_quantity: 5, title: 'L / Black' }], product_type: 'Apparel', vendor: 'Experience Wear', tags: ['cotton', 'new', 'space'] },
  { id: 'exp002', title: 'Experience Mug - Astronaut', images: [{ src: '/astromug.png' }], status: 'active', variants: [{ inventory_quantity: 30, title: 'Standard' }], product_type: 'Homeware', vendor: 'Experience Goods', tags: ['ceramic', 'gift'] },
  { id: 'exp003', title: 'Experience Hoodie - Nebula', images: [{ src: '/hoodie.png' }], status: 'draft', variants: [{ inventory_quantity: 8, title: 'S / Blue' }, { inventory_quantity: 12, title: 'M / Blue' }], product_type: 'Apparel', vendor: 'Experience Wear', tags: ['warm', 'winter'] },
  { id: 'exp004', title: 'Experience Cap - Rocket', images: [{ src: '/rocketcap.png' }], status: 'archived', variants: [{ inventory_quantity: 0, title: 'One Size' }], product_type: 'Accessory', vendor: 'Experience Gear', tags: ['summer', 'promo'] },
  { id: 'exp005', title: 'Experience Water Bottle - Stainless Steel', images: [{ src: '/waterbottle.png' }], status: 'active', variants: [{ inventory_quantity: 50, title: '750ml' }], product_type: 'Drinkware', vendor: 'Experience Goods', tags: ['eco', 'reusable'] },
  { id: 'exp006', title: 'Experience Socks - Alien Pattern', images: [{ src: '/alienpattern.png' }], status: 'active', variants: [{ inventory_quantity: 22, title: 'One Size' }], product_type: 'Apparel', vendor: 'Experience Wear', tags: ['fun', 'novelty'] },
  { id: 'exp007', title: 'Experience Backpack - Voyager', images: [{ src: '/voyagerbag.png' }], status: 'active', variants: [{ inventory_quantity: 18, title: 'Standard' }], product_type: 'Accessory', vendor: 'Experience Gear', tags: ['travel', 'durable'] },
  { id: 'exp008', title: 'Experience Poster - Solar System', images: [{ src: '/poster.png' }], status: 'draft', variants: [{ inventory_quantity: 40, title: 'A3' }], product_type: 'Decor', vendor: 'Experience Prints', tags: ['education', 'wall art'] },
  { id: 'exp009', title: 'Experience Keychain - Spaceship', images: [{ src: '/keychain.png' }], status: 'active', variants: [{ inventory_quantity: 100, title: 'Standard' }], product_type: 'Accessory', vendor: 'Experience Goods', tags: ['collectible'] },
  { id: 'exp010', title: 'Experience Phone Case - Mars Rover', images: [{ src: '/marsrover.png' }], status: 'active', variants: [{ inventory_quantity: 3, title: 'iPhone X' }, { inventory_quantity: 7, title: 'iPhone 12' }], product_type: 'Accessory', vendor: 'Experience Tech', tags: ['protective', 'mars'] },
  { id: 'exp011', title: 'Experience Beanie - Comet Trail', images: [], status: 'archived', variants: [{ inventory_quantity: 0, title: 'One Size' }], product_type: 'Apparel', vendor: 'Experience Wear', tags: ['winter', 'limited'] },
  { id: 'exp012', title: 'Experience Stickers Pack - Cosmic', images: [{ src: '/stickers.png' }], status: 'active', variants: [{ inventory_quantity: 200, title: 'Pack of 50' }], product_type: 'Stationery', vendor: 'Experience Prints', tags: ['decor', 'laptop'] },
  { id: 'exp013', title: 'Experience Scarf - Starry Night', images: [{ src: '/scarf.png' }], status: 'active', variants: [{ inventory_quantity: 12, title: 'Standard' }], product_type: 'Apparel', vendor: 'Experience Wear', tags: ['fashion', 'soft'] },
  { id: 'exp014', title: 'Experience Notebook - Constellation', images: [{ src: '/notebook.png' }], status: 'draft', variants: [{ inventory_quantity: 60, title: 'A5 Lined' }], product_type: 'Stationery', vendor: 'Experience Prints', tags: ['writing', 'journal'] },
  { id: 'exp015', title: 'Experience Desk Mat - Deep Space', images: [{ src: '/deskmat.png' }], status: 'active', variants: [{ inventory_quantity: 25, title: 'Large' }], product_type: 'Office', vendor: 'Experience Goods', tags: ['gaming', 'ergonomic'] },
  { id: 'exp016', title: 'Experience Puzzle - Earth View', images: [{ src: '/puzzle.png' }], status: 'active', variants: [{ inventory_quantity: 10, title: '1000 pieces' }], product_type: 'Toy', vendor: 'Experience Games', tags: ['family', 'challenge'] },
];

const ProductList = () => {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortOption, setSortOption] = useState('title-asc');
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProducts = async () => {
      setIsLoading(true); // Set loading true at the start
      setError(null); // Reset error
      try {
        const token = localStorage.getItem('access_token');
        const userDataString = localStorage.getItem('userData');
        let userRole = null;

        if (!token) {
          navigate('/'); // Redirect if no token
          setIsLoading(false);
          return;
        }
        
        if (userDataString) {
          try {
            const userData = JSON.parse(userDataString);
            userRole = userData.role;
          } catch (parseError) {
            console.error("Failed to parse user data from localStorage:", parseError);
            // Potentially set error or proceed as non-experience user
          }
        }

        if (userRole === 'experience') {
          console.log("Experience user detected, loading dummy products.");
          setProducts(dummyProductsList);
          setIsLoading(false);
          return; // Stop further execution for experience user
        }

        // Existing logic for admin and other users
        let endpoint = '/api/shopify/get_products';
        let params = {}; 

        if (userRole === 'admin') {
          endpoint = '/api/shopify/get_local_products';
          console.log("Admin user detected, fetching from local endpoint:", endpoint);
        } else {
           console.log("Non-admin/non-experience user or role unknown, fetching from Shopify endpoint:", endpoint);
        }

        const response = await axios.get(endpoint, {
          headers: { Authorization: `Bearer ${token}` },
          params: params
        });
        
        if (response.data && response.data.products) {
           setProducts(response.data.products);
        } else {
           console.warn("Product data not found in expected format:", response.data);
           setProducts([]);
           setError(t('products.fetchErrorFormat')); // Set an error if format is wrong
        }

      } catch (err) {
        console.error("Error fetching products:", err);
        setError(err.message);
        if (err.response && (err.response.status === 401 || err.response.status === 422)) {
          localStorage.removeItem('access_token'); // Clear token on auth error
          localStorage.removeItem('userData'); // Clear user data as well
          navigate('/'); // Redirect to login
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, [navigate, t]); // Added navigate and t to dependencies

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus, sortOption]);

  const handleProductClick = (productId) => {
    navigate(`/product/${productId}`);
  };

  const handleCheckboxChange = (productId) => {
    setSelectedProducts(prev => {
      if (prev.includes(productId)) {
        return prev.filter(id => id !== productId);
      } else {
        return [...prev, productId];
      }
    });
  };

  const handleOptimizeClick = async () => {
    if (selectedProducts.length === 0) {
      alert(t('products.selectAtLeastOne'));
      return;
    }

    setIsOptimizing(true);

    // Check user role for "experience" simulation
    const userDataString = localStorage.getItem('userData');
    let userRole = null;
    if (userDataString) {
        try {
            const userData = JSON.parse(userDataString);
            userRole = userData.role;
        } catch (parseError) {
            console.error("Failed to parse user data from localStorage for optimize click:", parseError);
        }
    }

    if (userRole === 'experience') {
      console.log("Simulating product optimization for experience user.");
      setTimeout(() => { // Simulate network delay
        setSelectedProducts([]);
        setIsOptimizing(false);
        alert(t('products.optimizeSuccessSimulated', 'Products optimization simulated successfully!'));
      }, 1000);
      return;
    }

    // Real optimization logic for other users
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        navigate('/');
        setIsOptimizing(false);
        return;
      }
      
      // Convert numbers to strings
      const productIdsAsStrings = selectedProducts.map(id => id.toString());
      
      const response = await axios.post(
        '/api/shopify/optimize_products', 
        { product_ids: productIdsAsStrings },
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const updatedProducts = await axios.get('/api/shopify/get_products', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProducts(updatedProducts.data.products);
      
      setSelectedProducts([]);
      alert(t('products.optimizeSuccess'));
    } catch (err) {
      console.error('Optimization error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        requestData: selectedProducts
      });
      setError(`${t('products.optimizeFailed')}: ${err.message}.`);
    } finally {
      setIsOptimizing(false);
    }
  };

  const formatVariants = (variants) => {
    // Group variants by their options
    const groupedVariants = variants.reduce((acc, variant) => {
      const [size, color] = variant.title.split(' / ');
      if (size) acc.sizes.add(size);
      if (color) acc.colors.add(color);
      return acc;
    }, { sizes: new Set(), colors: new Set() });

    return {
      sizes: Array.from(groupedVariants.sizes),
      colors: Array.from(groupedVariants.colors)
    };
  };

  const hasLowStock = (variants) => {
    return variants.some(variant => variant.inventory_quantity < 10);
  };

  // Add sort functionality
  const sortProducts = (products) => {
    const [field, direction] = sortOption.split('-');
    
    return [...products].sort((a, b) => {
      let comparison = 0;
      
      switch (field) {
        case 'title':
          comparison = a.title.localeCompare(b.title);
          break;
        case 'status':
          comparison = a.status.localeCompare(b.status);
          break;
        case 'type':
          comparison = a.product_type.localeCompare(b.product_type);
          break;
        case 'vendor':
          comparison = a.vendor.localeCompare(b.vendor);
          break;
        case 'inventory':
          comparison = (
            a.variants.reduce((sum, v) => sum + v.inventory_quantity, 0) - 
            b.variants.reduce((sum, v) => sum + v.inventory_quantity, 0)
          );
          break;
        default:
          comparison = 0;
      }
      
      return direction === 'asc' ? comparison : -comparison;
    });
  };

  // Add filter functionality
  const filterProducts = () => {
    let filteredProducts = products;
    
    // Apply search
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filteredProducts = filteredProducts.filter(product => 
        product.title.toLowerCase().includes(searchLower) ||
        (product.product_type && product.product_type.toLowerCase().includes(searchLower)) ||
        (product.vendor && product.vendor.toLowerCase().includes(searchLower)) ||
        (product.tags && Array.isArray(product.tags) && product.tags.some(tag => tag.toLowerCase().includes(searchLower)))
      );
    }
    
    // Apply status filter
    if (filterStatus !== 'all') {
      filteredProducts = filteredProducts.filter(product => product.status === filterStatus);
    }
    
    // Apply sort
    return sortProducts(filteredProducts);
  };

  const filteredAndSortedProducts = filterProducts();
  const getStockStatus = (variants) => {
    const totalInventory = variants.reduce((sum, v) => sum + v.inventory_quantity, 0);
    if (totalInventory <= 0) return 'out-of-stock';
    if (totalInventory < 10) return 'low-stock';
    return 'in-stock';
  };

  // Calculate pagination variables
  const totalItems = filteredAndSortedProducts.length;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedProducts = filteredAndSortedProducts.slice(startIndex, endIndex);

  // Pagination handler
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo(0, 0);
    }
  };

  if (error) return <div className="una-product-error">{t('common.error')}: {error}</div>;

  if (isLoading) {
    return (
      <div className="una-dashboard-loading-screen">
        <div className="una-loading-content">
          <div className="una-loading-logo">
            <img src="/bgless_logo.png" alt="Unanimity AI Logo" />
          </div>
          <h2>{t('products.loadingTitle')}</h2>
          <div className="una-loading-progress">
            <div className="una-progress-bar">
              <div className="una-progress-glow"></div>
            </div>
          </div>
          <div className="una-loading-metrics">
            <div className="una-metric-item">
              <FaStore />
              <span>{t('products.fetchingStore')}</span>
            </div>
            <div className="una-metric-item">
              <FaBoxOpen />
              <span>{t('products.processingInventory')}</span>
            </div>
            <div className="una-metric-item">
              <FaBarcode />
              <span>{t('products.loadingVariants')}</span>
            </div>
            <div className="una-metric-item">
              <FaTags />
              <span>{t('products.processingTags')}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="una-products-container">
      <div className="una-products-header">
        <div className="una-products-title">
          <FaStore />
          <h1>{t('products.title')}</h1>
          <span className="una-products-count">{products.length} {t('products.itemsCount')}</span>
        </div>
        
        <div className="una-products-actions">
          <button 
            className={`una-optimize-button ${selectedProducts.length > 0 ? 'active' : ''}`}
            onClick={handleOptimizeClick}
            disabled={isOptimizing || selectedProducts.length === 0}
          >
            {isOptimizing ? (
              <>
                <div className="una-spinner"></div>
                {t('products.optimizing')}
              </>
            ) : (
              <>
                <FaTag />
                {t('products.optimizeSelected')} {selectedProducts.length > 0 && `(${selectedProducts.length})`}
              </>
            )}
          </button>
          <button 
            className="una-create-button"
            onClick={() => navigate('/products/create')}
          >
            <FaPlus /> {t('products.create')}
          </button>
        </div>
      </div>

      <div className="una-products-toolbar">
        <div className="una-search-wrapper">
          <FaSearch className="una-search-icon" />
          <input
            type="text"
            className="una-search-input"
            placeholder={t('products.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="una-clear-search" onClick={() => setSearchTerm('')}>
              ×
            </button>
          )}
        </div>
        
        <div className="una-filters">
          <div className="una-filter-group">
            <label className="una-filter-label">
              <FaFilter /> {t('products.filter')}
            </label>
            <select 
              className="una-filter-select"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="all">{t('products.allStatuses')}</option>
              <option value="active">{t('products.active')}</option>
              <option value="draft">{t('products.draft')}</option>
              <option value="archived">{t('products.archived')}</option>
            </select>
          </div>
          
          <div className="una-filter-group">
            <label className="una-filter-label">
              <FaSortAmountDown /> {t('products.sort')}
            </label>
            <select 
              className="una-filter-select"
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value)}
            >
              <option value="title-asc">{t('products.sortTitleAsc')}</option>
              <option value="title-desc">{t('products.sortTitleDesc')}</option>
              <option value="status-asc">{t('products.sortStatusAsc')}</option>
              <option value="type-asc">{t('products.sortTypeAsc')}</option>
              <option value="inventory-desc">{t('products.sortInventoryDesc')}</option>
              <option value="inventory-asc">{t('products.sortInventoryAsc')}</option>
            </select>
          </div>
          
          <div className="una-selected-info">
            {selectedProducts.length > 0 && (
              <span>
                {selectedProducts.length} {t('products.selected')}
                <button 
                  className="una-clear-selection"
                  onClick={() => setSelectedProducts([])}
                >
                  {t('products.clearSelection')}
                </button>
              </span>
            )}
          </div>
        </div>
      </div>

      {!totalItems ? (
        <div className="una-products-empty-state">
          <FaBoxes className="empty-icon" />
          <p>
            {searchTerm || filterStatus !== 'all'
              ? t('products.noProductsFiltered')
              : t('products.noProducts')
            }
          </p>
          {(searchTerm || filterStatus !== 'all') && (
            <button
              className="una-clear-filters"
              onClick={() => {
                setSearchTerm('');
                setFilterStatus('all');
              }}
            >
              {t('products.clearFilters')}
            </button>
          )}
        </div>
      ) : (
        <div className="una-products-table-wrapper">
          <table className="una-products-table">
            <thead>
              <tr>
                <th className="una-table-checkbox-cell">
                  <input
                    type="checkbox"
                    aria-label={t('products.selectAll')}
                    class="una-product-checkbox"
                  />
                </th>
                <th className="una-table-image-header"></th>
                <th>{t('products.productTitle')}</th>
                <th>{t('products.status')}</th>
                <th>{t('products.inventory')}</th>
                <th>{t('products.type')}</th>
                <th>{t('products.vendor')}</th>
                <th>{t('products.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {paginatedProducts.map((product) => {
                const stockStatus = getStockStatus(product.variants);
                const totalInventory = product.variants.reduce((sum, v) => sum + v.inventory_quantity, 0);

                return (
                  <tr key={product.id} className={`una-product-row ${stockStatus}`}>
                    <td className="una-table-checkbox-cell">
                      <input
                        type="checkbox"
                        className="una-product-checkbox"
                        checked={selectedProducts.includes(product.id)}
                        onChange={() => handleCheckboxChange(product.id)}
                        aria-label={`${t('products.select')} ${product.title}`}
                      />
                    </td>
                    <td className="una-table-image-cell">
                      <div className="una-product-image-wrapper-table">
                        {product.images && product.images.length > 0 ? (
                          <img
                            src={product.images[0].src}
                            alt={product.title}
                            className="una-product-image-table"
                            loading="lazy"
                            onClick={() => handleProductClick(product.id)}
                          />
                        ) : (
                          <div className="una-product-no-image-table" onClick={() => handleProductClick(product.id)}>
                            <FaBoxOpen />
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="una-table-title-cell">
                      <span 
                        className="una-product-title-link" 
                        onClick={() => handleProductClick(product.id)}
                      >
                        {product.title}
                      </span>
                      {Array.isArray(product.tags) && product.tags.length > 0 && (
                        <div className="una-product-tags-inline">
                          {product.tags.slice(0, 2).map((tag, index) => (
                            <span key={index} className="una-product-tag-inline">
                              {tag.trim()}
                            </span>
                          ))}
                          {product.tags.length > 2 && (
                            <span className="una-product-tag-inline more">
                              +{product.tags.length - 2}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="una-table-status-cell">
                      <span className={`una-status-badge status-${product.status ? product.status.toLowerCase() : 'unknown'}`}>
                        {t(`products.status_${product.status ? product.status.toLowerCase() : 'unknown'}`, product.status || t('common.unknown'))}
                      </span>
                    </td>
                    <td className={`una-table-inventory-cell ${stockStatus}`}>
                      <span className="una-stock-indicator-table">
                        {stockStatus === 'out-of-stock' && (
                          <><FaExclamationTriangle /> {t('products.outOfStock')}</>
                        )}
                        {stockStatus === 'low-stock' && (
                           <><FaExclamationTriangle /> {totalInventory} {t('products.lowStockIndicator')}</>
                        )}
                        {stockStatus === 'in-stock' && (
                          <>{totalInventory} {t('products.inStockIndicator')}</>
                        )}
                      </span>
                    </td>
                    <td className="una-table-type-cell">{product.product_type || '-'}</td>
                    <td className="una-table-vendor-cell">{product.vendor || '-'}</td>
                    <td className="una-table-actions-cell">
                       <button 
                         className="una-action-button-table edit" 
                         onClick={() => handleProductClick(product.id)}
                         aria-label={`${t('products.edit')} ${product.title}`}
                       >
                         {t('products.edit')}
                       </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      
      {totalPages > 1 && (
        <div className="una-pagination-controls">
          <button
            className="una-pagination-button prev"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            aria-label={t('common.previousPage')}
          >
            <FaChevronLeft />
            <span>{t('common.previous')}</span>
          </button>

          <span className="una-pagination-info">
            {t('common.page')} {currentPage} {t('common.of')} {totalPages}
          </span>

          <button
            className="una-pagination-button next"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            aria-label={t('common.nextPage')}
          >
             <span>{t('common.next')}</span>
            <FaChevronRight />
          </button>
        </div>
      )}

      {totalItems > 0 && (
        <div className="una-products-footer">
          <span className="una-showing-count">
            {t('products.showingRange', {
              start: startIndex + 1,
              end: Math.min(endIndex, totalItems),
              total: totalItems
            })}
          </span>
          {(searchTerm || filterStatus !== 'all') && (
            <button
              className="una-clear-filters-btn"
              onClick={() => {
                setSearchTerm('');
                setFilterStatus('all');
              }}
            >
              {t('products.clearFilters')}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductList;